import { type MediaAssetType } from "@prismicio/editor-ui"
import { createContext, useContext } from "react"

export type EditorConfig = {
  baseUrl: URL
  embedApiEndpoint: URL
  unsplashApiBaseUrl: URL
  authStrategy: AuthStrategy
} & OptionalConfigs

// Optional configs that, when specified, need specific, mandatory (potentially shared) dependencies.
type OptionalConfigs = SearchDocuments &
  MediaLibrary &
  UIDField &
  Analytics &
  ErrorTracking &
  IntegrationFields &
  LayoutConfig &
  UserService

type UserService = {
  userServiceBaseUrl?: URL
}

type MediaLibrary =
  | { mediaLibrary?: never }
  /* If specified, the editor enables the Media library. */
  | {
      mediaLibrary: true
      assetsApiBaseUrl: URL
      repository: string
      coreApiBaseUrl: URL
      userServiceBaseUrl: URL
    }

type UIDField =
  | { uidField?: never }
  /* If specified, the editor enables the UID field remote unicity check */
  | {
      uidField: true
      coreApiBaseUrl: URL
      documentId?: string
      localeId: string
      customTypeId: string
    }

type SearchDocuments =
  | { searchDocuments?: never }
  /* If specified, the editor enables the document search in link fields, etc. */
  | {
      searchDocuments: true
      coreApiBaseUrl: URL
      customTypesApiBaseUrl: URL
      repository: string
      status: "published" | "release" | "archived" | "unclassified"
      localeId?: string
      customTypeId?: string
      documentSearchBaseUrl: URL
    }

type IntegrationFields =
  | { integrationFields?: never }
  /* If specified, the editor enables the integration fields. */
  | {
      integrationFields: true
      integrationFieldsApiBaseUrl: URL
      repository: string
    }

interface LayoutConfig {
  /**
   * Teaches fields about their containing scrolling element.
   */
  scrollOffsetTop?: number
}

interface OnAnalyticsEvent {
  (
    event: "Media Library Search",
    args: {
      assetType: MediaAssetType // asset type filtering used when media library was opened
      mode: "browse" | "select" // browse = Media Library is opened in browsing mode on a separate url | selection = Media Library is opened in selection mode inside a modal
      searchQuery: string // search query typed by user
      numberOfWords: string // number of words in search query
      numberOfResults: string // number of results returned for search query
      timeToGetResults: string // time in ms taken from search request fired to getting a response, includes request related operations
      tags: string // comma separated list of tags used by user to filter results
    }
  ): void
  (
    event: "Media Added To Page",
    args: {
      assetType: MediaAssetType // asset type filtering used when media library was opened
      searchQuery: string // search query typed by user
      numberOfWords: string // number of words in search query
      numberOfResults: string // number of results returned for search query
      searchPosition: string // where the item was in the search result i.e. what is the first, tenth, twentieth item selected, starts at 1
      mediaIds: string // comma separated list of selected media ids
      timeToAddMediaToPage: string // time in ms taken from opening Media Library modal to selecting a media
      tags: string // comma separated list of tags used by user to filter results
    }
  ): void
  (event: "Unsplash Image Searched" | "Unsplash Image Added"): void
}

interface Analytics {
  /* Asks the editor to send an analytics event. */
  onAnalyticsEvent?: OnAnalyticsEvent
}

interface ErrorTracking {
  /** Asks the editor to track this Error in its Error monitoring solution. */
  onTrackError?: (error: Error) => void
}

export type AuthStrategy = "cookie" | "token"

export const EditorConfigContext = createContext<EditorConfig | undefined>(
  undefined
)

export function useEditorConfig(): EditorConfig {
  const config = useContext(EditorConfigContext)
  if (!config) throw new Error("Missing EditorConfigContext.Provider")
  return config
}

class ConfigError extends Error {}

export function throwConfigError(configKey: string): never {
  const error = new ConfigError(`Missing EditorConfig.${configKey}`)
  if (import.meta.env.DEV) console.debug(error)
  throw error
}

// React is very eager to print errors, even errors that were caught by an Error Boundary.
// https://github.com/facebook/react/issues/15069
export function suppressErrors() {
  const url = new URL(location.href)
  const isDebugMode = url.searchParams.has("debug")

  // Don't suppress anything if someone added the debug query string to debug something in production.
  if (isDebugMode) return

  // In prod mode, the error is logged with console.error, suppress that.
  const consoleError = console.error.bind(console)
  console.error = (...data: unknown[]) => {
    if (data[0] instanceof ConfigError) return
    if (errorIsTipTapFlushSync(data)) return

    consoleError(...data)
  }

  // In dev mode, the error is thrown, suppress that.
  window.addEventListener("error", (evt) => {
    if (evt.error instanceof ConfigError) evt.preventDefault()
    if (errorIsTipTapListContent(evt.error)) evt.preventDefault()
  })
}

// This is required to ignore tiptap's flushsync issues. Remove when fixed.
// https://github.com/ueberdosis/tiptap/issues/3764
function errorIsTipTapFlushSync(data: unknown[]) {
  const flushSyncErrorMessage =
    "Warning: flushSync was called from inside a lifecycle method"

  const errorMessageIsFlushsync =
    typeof data[0] === "string" && data[0].includes(flushSyncErrorMessage)

  const stackTraceIncludesRichText =
    typeof data[1] === "string" && data[1].includes("RichTextField")

  return errorMessageIsFlushsync && stackTraceIncludesRichText
}

/**
 * This is required to ignore tiptap's list content issues in single paragraph mode.
 * The content and behaviour matches the expected behaviour, so we can safely ignore these errors.
 *
 * See bug fix attempts in PBD-1096: https://linear.app/prismic/issue/PBD-1096/%5Bbug%5D-rich-text-in-single-paragraph-mode-should-not-allow-paragraph
 */
function errorIsTipTapListContent(error: unknown) {
  const listContentErrorMessage =
    /Invalid content for node document: <(bulletList|orderedList)\(listItem*/

  const errorMessage = (error as { message: unknown }).message

  const errorMessageIsListContent = Boolean(
    typeof errorMessage === "string" &&
      errorMessage.match(listContentErrorMessage)
  )

  return errorMessageIsListContent
}
